import { useEffect, useRef } from 'react';

/**
 * Scroll motion layer, the same contract as apps/oneasia-corp and
 * apps/stackcombo-corp. Three effects, opted into with data attributes:
 *   data-reveal             fade + rise once the element enters the viewport
 *   data-reveal-delay="90"  optional ms delay, to stagger a row of siblings
 *   data-scrub="left|right" kinetic chapter word slides in as you scroll
 *   data-parallax="0.05"    image drifts against the scroll direction
 *
 * Every effect writes inline transform/opacity, so none of these attributes may
 * sit on an element that also carries a CSS entrance animation — put the
 * entrance on a wrapper and the scroll attribute on a child.
 *
 * The whole layer bails out under `prefers-reduced-motion: reduce`.
 */
export function useScrollMotion<T extends HTMLElement = HTMLDivElement>() {
  const rootRef = useRef<T | null>(null);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const cleanups: Array<() => void> = [];
    const frame = requestAnimationFrame(() => {
      // ---- 1. Scroll reveal ------------------------------------------------
      const revealEls = Array.from(root.querySelectorAll<HTMLElement>('[data-reveal]'));
      if (revealEls.length) {
        const revealObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              const el = entry.target as HTMLElement;
              el.style.opacity = '1';
              el.style.transform = 'none';
              revealObserver.unobserve(el);
            });
          },
          { threshold: 0.15 }
        );

        revealEls.forEach((el) => {
          // Anything already near the top of the fold is never hidden.
          if (el.getBoundingClientRect().top < window.innerHeight * 0.85) return;
          const delay = Number(el.dataset.revealDelay) || 0;
          el.style.transition =
            `opacity 0.8s ease ${delay}ms, ` +
            `transform 0.8s cubic-bezier(0.16,1,0.3,1) ${delay}ms`;
          el.style.opacity = '0';
          el.style.transform = 'translateY(48px) scale(0.98)';
          revealObserver.observe(el);
        });

        cleanups.push(() => revealObserver.disconnect());
      }

      // ---- 2 + 3. Kinetic word scrub, parallax -----------------------------
      const scrubEls = Array.from(root.querySelectorAll<HTMLElement>('[data-scrub]'));
      const parallaxEls = Array.from(root.querySelectorAll<HTMLElement>('[data-parallax]'));

      if (scrubEls.length || parallaxEls.length) {
        let ticking = false;

        const update = () => {
          ticking = false;
          const vh = window.innerHeight;
          const scrubDistance = Math.min(140, window.innerWidth * 0.18);

          scrubEls.forEach((el) => {
            const rect = el.getBoundingClientRect();
            const p = Math.min(Math.max((vh - rect.top) / (vh * 0.7), 0), 1);
            const dir = el.dataset.scrub === 'right' ? 1 : -1;
            el.style.transform = `translateX(${dir * (1 - p) * scrubDistance}px)`;
            el.style.opacity = String(0.15 + 0.85 * p);
          });

          parallaxEls.forEach((el) => {
            const amount = Number(el.dataset.parallax) || 0;
            const rect = el.getBoundingClientRect();
            const c = rect.top + rect.height / 2 - vh / 2;
            el.style.transform = `translateY(${-c * amount}px)`;
          });
        };

        const onScroll = () => {
          if (ticking) return;
          ticking = true;
          requestAnimationFrame(update);
        };

        update();
        window.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', onScroll, { passive: true });
        cleanups.push(() => {
          window.removeEventListener('scroll', onScroll);
          window.removeEventListener('resize', onScroll);
        });
      }
    });

    return () => {
      cancelAnimationFrame(frame);
      cleanups.forEach((fn) => fn());
    };
  }, []);

  return rootRef;
}

export default useScrollMotion;
